import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import SEO, { orgJsonLd, breadcrumbJsonLd } from "../components/SEO";
import Reveal from "../components/Reveal";
import Icon from "../components/Icon";
import LeadForm from "../components/LeadForm";
import { SERVICES, ALL_CITIES } from "../data/siteData";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 5);
  const topCities = ALL_CITIES.filter((c) => c.tier === "Tier 1").slice(0, 8);

  return (
    <>
      <SEO
        title={`${service.name} | Best ${service.name.split(" &")[0]} Firm in India — Complisure Counsultancy`}
        description={`${service.short} Trusted by 800+ clients across Hyderabad, Bengaluru, Delhi NCR, Chennai and 27 more cities.`}
        canonical={`https://compliance-capital-1.preview.emergentagent.com/services/${service.slug}`}
        jsonLd={[
          orgJsonLd,
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.name, path: `/services/${service.slug}` },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.name,
            provider: { "@type": "Organization", name: "Complisure Counsultancy" },
            areaServed: "IN",
            description: service.short,
          },
        ]}
      />
      <section className="relative overflow-hidden pb-16 pt-36">
        <div className="blob blob-float left-[-8%] top-0 h-96 w-96 bg-brand-blue/10" />
        <div className="dot-grid absolute inset-0 opacity-[0.1]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <nav className="flex items-center gap-1.5 text-xs text-brand-gray" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-brand-blue">Home</Link>
              <ChevronRight className="h-3 w-3" />
              <Link to="/services" className="hover:text-brand-blue">Services</Link>
              <ChevronRight className="h-3 w-3" />
              <span className="text-brand-navy">{service.name}</span>
            </nav>
          </Reveal>

          <div className="mt-8 grid items-start gap-12 lg:grid-cols-[1.2fr_1fr]">
            <Reveal>
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-blue to-brand-sky text-white shadow-lg shadow-brand-blue/25">
                <Icon name={service.icon} className="h-6 w-6" />
              </span>
              <h1 className="mt-6 font-display text-4xl font-extrabold tracking-[-0.02em] text-brand-navy sm:text-5xl">{service.name}</h1>
              <p className="mt-5 text-base leading-relaxed text-brand-gray sm:text-lg">{service.desc}</p>

              <h2 className="mt-10 font-display text-2xl font-bold tracking-tight text-brand-navy">What's included</h2>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {service.deliverables.map((d) => (
                  <li key={d} data-testid={`deliverable-${d.slice(0, 16).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="glass flex items-start gap-3 rounded-2xl p-4 text-sm text-brand-navy/85">
                    <Icon name="CheckCircle2" className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /> {d}
                  </li>
                ))}
              </ul>

              <h2 className="mt-12 font-display text-2xl font-bold tracking-tight text-brand-navy">Available in these cities</h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {topCities.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/locations/${c.stateSlug}/${c.slug}`}
                    className="rounded-full border border-brand-blue/15 bg-white/70 px-4 py-2 text-xs font-medium text-brand-navy transition-colors hover:bg-brand-ice hover:text-brand-blue"
                  >
                    {service.name.split(" &")[0]} in {c.name}
                  </Link>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.12} className="lg:sticky lg:top-28">
              <LeadForm defaultService={service.name} sourcePage={`/services/${service.slug}`} />
            </Reveal>
          </div>

          <div className="mt-20">
            <h2 className="font-display text-2xl font-bold tracking-tight text-brand-navy">Related services</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {others.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="glass card-lift group rounded-2xl p-5">
                  <Icon name={s.icon} className="mb-3 h-5 w-5 text-brand-blue" />
                  <p className="text-sm font-semibold text-brand-navy group-hover:text-brand-blue">{s.name}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-blue">View <ArrowRight className="h-3 w-3" /></span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
